// Numeric multiplication evaluation used in loop-expanded plain text.
// Example: "2 * 4px" -> "8px" outside calc()/min()/max()/clamp().
import type { NumericOperand } from './types'
import { collectCssMathFunctionRanges, isIndexInsideRanges } from './arithmetic-ranges'

// Evaluate `a * b` expressions when both sides are numeric tokens.
export function evaluateMultiplicationExpressions(source: string, variables: Record<string, string>): string {
  // Return source unchanged when multiplication operator is absent.
  if (!source.includes('*')) {
    return source
  } else {
    // Continue multiplication evaluation flow.
  }

  const cssMathRanges = collectCssMathFunctionRanges(source)
  const withWrappedMultiplications = source.includes('(')
    ? evaluateWrappedMultiplications(source, variables, cssMathRanges)
    : source
  // Regex captures lightweight multiplication expressions like `1px * var(--gap)`.
  const output = withWrappedMultiplications.replace(
    /([a-zA-Z0-9_.()%\-]+)\s*\*\s*([a-zA-Z0-9_.()%\-]+)/g,
    (match, leftToken, rightToken, offset) => {
    // Keep expressions untouched inside CSS math functions (e.g. calc/min/max/clamp).
    if (isIndexInsideRanges(offset, cssMathRanges)) {
      return match
    } else {
      // Continue arithmetic parsing outside CSS math ranges.
    }

    const left = parseNumericOperand(leftToken, variables)
    const right = parseNumericOperand(rightToken, variables)
    // Keep expression unchanged when one side is not numeric.
    if (!left || !right) {
      return match
    } else {
      // Continue multiplication unit resolution.
    }

    const unit = resolveMultiplicationUnit(left.unit, right.unit)
    // Keep expression unchanged when unit combination is ambiguous.
    if (unit === null) {
      return match
    } else {
      // Continue numeric multiplication formatting.
    }

    const result = left.value * right.value
    return `${formatNumericValue(result)}${unit}`
  },
  )

  // Return arithmetic-transformed source text.
  return output
}

// Evaluate `(<a> * <b>)unit` forms like `($i * $j)px` after placeholder replacement.
function evaluateWrappedMultiplications(
  source: string,
  variables: Record<string, string>,
  cssMathRanges: Array<{ start: number; end: number }>,
): string {
  // Regex captures parenthesized multiplication with explicit trailing unit.
  return source.replace(
    /\(\s*([a-zA-Z0-9_.()%\-]+)\s*\*\s*([a-zA-Z0-9_.()%\-]+)\s*\)([a-zA-Z%]+)/g,
    (match, leftToken, rightToken, trailingUnit, offset) => {
      // Keep expressions untouched inside CSS math functions.
      if (isIndexInsideRanges(offset, cssMathRanges)) {
        return match
      } else {
        // Continue wrapped multiplication evaluation.
      }

      const left = parseNumericOperand(leftToken, variables)
      const right = parseNumericOperand(rightToken, variables)
      // Keep expression unchanged when one side is not numeric.
      if (!left || !right) {
        return match
      } else {
        // Continue wrapped unit compatibility checks.
      }

      const unit = resolveMultiplicationUnit(left.unit, right.unit)
      // Keep expression unchanged when base units cannot be resolved.
      if (unit === null) {
        return match
      } else if (unit && unit !== trailingUnit) {
        // Keep expression unchanged when trailing unit conflicts with result unit.
        return match
      } else {
        // Continue wrapped multiplication formatting.
      }

      const result = left.value * right.value
      return `${formatNumericValue(result)}${trailingUnit}`
    },
  )
}

// Parse token into numeric value/unit and resolve `var(--x)` operands.
function parseNumericOperand(token: string, variables: Record<string, string>): NumericOperand | null {
  const trimmed = token.trim()
  const variableName = parseVarReferenceName(trimmed)
  // Resolve variable operand before numeric parsing.
  if (variableName) {
    const value = variables[variableName]
    // Reject unresolved variable references in multiplication.
    if (!value) {
      return null
    } else {
      // Return recursively parsed variable value operand.
      return parseNumericOperand(value, variables)
    }
  } else {
    // Continue with literal numeric operand parsing.
  }

  const literal = parseNumericLiteral(trimmed)
  // Reject non-numeric tokens such as selector names.
  if (!literal) {
    return null
  } else {
    // Return parsed numeric literal operand.
    return literal
  }
}

// Resolve resulting unit for multiplication of two numeric operands.
function resolveMultiplicationUnit(leftUnit: string, rightUnit: string): string | null {
  // Keep unitless result when both operands are unitless.
  if (!leftUnit && !rightUnit) {
    return ''
  } else {
    // Continue resolving mixed unit combinations.
  }

  // Keep left unit when right side is unitless.
  if (leftUnit && !rightUnit) {
    return leftUnit
  } else {
    // Continue resolving remaining unit combinations.
  }

  // Keep right unit when left side is unitless.
  if (!leftUnit && rightUnit) {
    return rightUnit
  } else {
    // Continue checking same-unit combinations.
  }

  // Keep a single unit when both sides share the same CSS unit.
  if (leftUnit === rightUnit) {
    return leftUnit
  } else {
    // Return null for unsupported mixed units.
    return null
  }
}

// Format numbers without trailing decimal zeroes.
function formatNumericValue(value: number): string {
  const rounded = Number.parseFloat(value.toFixed(6))
  // Return compact numeric output.
  return Number.isInteger(rounded) ? String(rounded) : String(rounded)
}

// Parse `var(--name)` and return variable name when syntax is valid.
function parseVarReferenceName(token: string): string | null {
  // Reject tokens that are not wrapped with `var(...)`.
  if (!token.startsWith('var(') || !token.endsWith(')')) {
    return null
  } else {
    // Continue var-token inner-name parsing.
  }

  const inner = token.slice(4, -1).trim()
  // Reject custom-property tokens without `--` prefix.
  if (!inner.startsWith('--')) {
    return null
  } else {
    // Continue custom-property identifier validation.
  }

  const first = inner[2] ?? ''
  // Reject names with invalid first identifier char.
  if (!isIdentifierStart(first)) {
    return null
  } else {
    // Continue identifier suffix validation.
  }

  // Validate remaining chars in custom-property name.
  for (let index = 3; index < inner.length; index += 1) {
    if (!isIdentifierChar(inner[index])) {
      return null
    } else {
      // Continue identifier char validation.
    }
  }

  // Return parsed variable name from valid var token.
  return inner
}

// Parse numeric literal with optional unit suffix.
function parseNumericLiteral(token: string): NumericOperand | null {
  let index = 0
  // Parse optional sign at numeric literal start.
  if (token[index] === '-') {
    index += 1
  } else {
    // Continue unsigned numeric parsing.
  }

  let hasDigits = false
  // Parse integer part digits.
  while (index < token.length && isDigit(token[index])) {
    hasDigits = true
    index += 1
  }

  // Parse optional decimal separator and fraction digits.
  if (token[index] === '.') {
    index += 1
    while (index < token.length && isDigit(token[index])) {
      hasDigits = true
      index += 1
    }
  } else {
    // Continue unit parsing without decimal part.
  }

  // Reject tokens without numeric digits.
  if (!hasDigits) {
    return null
  } else {
    // Continue parsing optional unit suffix.
  }

  const numberPart = token.slice(0, index)
  const unit = token.slice(index)
  // Reject units containing invalid chars.
  if (unit && !isUnitToken(unit)) {
    return null
  } else {
    // Continue numeric-literal finalization.
  }

  const value = Number.parseFloat(numberPart)
  // Reject NaN parse output from malformed numeric token.
  if (Number.isNaN(value)) {
    return null
  } else {
    // Return parsed numeric operand.
    return { value, unit }
  }
}

// Validate CSS-like identifier first character.
function isIdentifierStart(char: string): boolean {
  return (
    (char >= 'a' && char <= 'z')
    || (char >= 'A' && char <= 'Z')
    || char === '_'
  )
}

// Validate CSS-like identifier continuation characters.
function isIdentifierChar(char: string): boolean {
  return isIdentifierStart(char) || isDigit(char) || char === '-'
}

// Validate decimal digit characters.
function isDigit(char: string): boolean {
  return char >= '0' && char <= '9'
}

// Validate unit suffix with alpha chars or `%`.
function isUnitToken(unit: string): boolean {
  // Reject empty units in validator context.
  if (!unit) {
    return false
  } else {
    // Continue unit-char validation.
  }

  for (let index = 0; index < unit.length; index += 1) {
    const char = unit[index]
    const isAlpha = (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z')
    if (!isAlpha && char !== '%') {
      return false
    } else {
      // Continue unit validation scan.
    }
  }

  // Return true for valid unit tokens.
  return true
}
